// The mapping response — a FAITHFUL transcription of SPEC §5.1.
//
// SPEC §5 carries the banner "Engine internals are being prototyped, behavior
// is spec-final. The mapping contract (proposed mapping shape, confidence
// tiers, fallback-to-manual, deterministic transforms, validation rules) holds
// regardless." That is what this file encodes: the response *shape* and the
// tier thresholds, not the engine.
//
// ⚠️ ONE KNOWN OPEN ITEM, deliberately NOT pre-empted here. PE-1886 flags that
// the mapping/upload contract likely needs a per-file role signal
// (`sourceFileHint`): the P0 spike found Mailchimp's consent state is carried
// by WHICH of its four exported status files a row came from, not by any
// column, so no amount of header inspection can recover it
// (decisions.md#D-MAPPING-ENGINE-SHAPE, "Consent-trap finding"). That signal
// is NOT invented here — §5.1 does not define it, and guessing its name/shape
// would be exactly the improvisation four tracks cannot afford. It is additive
// (a new optional input field), which is the cheap direction to change later.
// Tracked in the handoff as a gate caveat.

import { z } from 'zod';

// PE-1899 — O3's durable column list reuses the SAME shape O2's header line
// denormalizes, so the two cannot drift. No cycle: dataset.ts imports only
// from fields.ts.
import { denormalizedColumnMappingSchema } from './dataset.js';

// ---------------------------------------------------------------------------
// §5.1 — confidence tiers
// ---------------------------------------------------------------------------

/**
 * SPEC §5.1's three bands, verbatim:
 *   - "High confidence (≥0.9): auto-mapped, user confirms with one click."
 *   - "Medium confidence (0.5–0.9): mapped but highlighted for review."
 *   - "Low confidence or unmapped: user must choose manually."
 *
 * Exported as named constants so the wizard's confidence zones (Milestone D,
 * PE-1906) and the backend's auto-map decision read the SAME numbers instead
 * of each hardcoding 0.9. A drift between those two is a silent
 * mis-auto-mapping.
 */
export const MAPPING_CONFIDENCE_HIGH_THRESHOLD = 0.9;
export const MAPPING_CONFIDENCE_MEDIUM_THRESHOLD = 0.5;

export const mappingConfidenceTierSchema = z.enum(['high', 'medium', 'low']);
export type MappingConfidenceTier = z.infer<typeof mappingConfidenceTierSchema>;

/**
 * The §5.1 banding, as a function so no consumer re-implements the
 * boundary conditions. Note the boundaries follow the SPEC's own notation:
 * `≥0.9` is high, and the medium band's lower edge is inclusive (0.5).
 *
 * An unmapped column (`field: null`) is always 'low' regardless of the number
 * — §5.1 groups "low confidence OR unmapped" into the same manual-choice
 * bucket. Callers should pass the proposal, not a bare number, to get that.
 */
export function mappingConfidenceTier(confidence: number): MappingConfidenceTier {
  if (confidence >= MAPPING_CONFIDENCE_HIGH_THRESHOLD) return 'high';
  if (confidence >= MAPPING_CONFIDENCE_MEDIUM_THRESHOLD) return 'medium';
  return 'low';
}

/**
 * Tier for a whole proposal, honoring §5.1's "Low confidence **or unmapped**"
 * grouping: a null field can never be auto-mapped, whatever its score.
 */
export function proposalTier(proposal: ColumnMappingProposal): MappingConfidenceTier {
  if (proposal.field === null) return 'low';
  return mappingConfidenceTier(proposal.confidence);
}

// ---------------------------------------------------------------------------
// §5.1 — the proposed mapping
// ---------------------------------------------------------------------------

/**
 * One entry of §5.1's response. The SPEC's example:
 *
 * ```json
 * {
 *   "First Name":     { "field": "firstName",     "confidence": 0.98 },
 *   "T-Shirt Size":   { "field": null,            "confidence": 0.0  },
 *   "Unsubscribed":   { "field": "emailConsent",  "confidence": 0.72 }
 * }
 * ```
 *
 * `field` is **nullable, not optional** — the SPEC emits an explicit `null`
 * for an unrecognized column rather than omitting the key. That distinction is
 * load-bearing: an absent key means "the engine never saw this column", a null
 * field means "the engine saw it and declined to map it", and the wizard must
 * render those differently (the second gets the custom-field / tag / skip
 * choice per §4.2).
 *
 * `field` is typed as a plain string rather than an enum of contact fields.
 * The target-field enum is Milestone C's to own (it spans standard contact
 * fields plus the custom-field and tag routes in §4.2/§7), and §5.1 does not
 * enumerate it. Pinning a guessed enum here would force a contract change the
 * moment C discovers the real list.
 *
 * What IS enumerated is the deny-list: §5.4.2's "Rejected mapping targets"
 * (`totalTransactionCount`, `totalTransactionValue`, `created_at`, `updated_at`,
 * `id`, `merchant`, `merchantID`, `emailMerchantKey`, `phoneMerchantKey`,
 * `is_verified`) — "The mapping UI does not offer them; the backend rejects
 * them if sent." That is enforced server-side at C, not by this shape; it is
 * not encoded here because a deny-list in the contract would drift from the
 * writer's real one.
 */
export const columnMappingProposalSchema = z.object({
  field: z.string().nullable(),
  confidence: z.number().min(0).max(1),
});
export type ColumnMappingProposal = z.infer<typeof columnMappingProposalSchema>;

/**
 * §5.1's full response: keyed by the source file's column header, exactly as
 * it appeared in the file (the SPEC's example keys are the raw headers —
 * `"First Name"`, `"Email Address"` — preserving original casing and spacing,
 * since those are what the mapping UI shows on the left-hand side per §4.2).
 */
export const proposedMappingSchema = z.record(
  z.string(),
  columnMappingProposalSchema,
);
export type ProposedMapping = z.infer<typeof proposedMappingSchema>;

// ---------------------------------------------------------------------------
// §5.2 — source fingerprinting (part of the mapping response)
// ---------------------------------------------------------------------------

/**
 * SPEC §5.2: "When we recognize a source by its headers, we can pre-fill the
 * mapping without calling the AI at all." §5.2 names the fingerprintable
 * sources (Mailchimp, Givebutter, Klaviyo) and the non-fingerprintable ones,
 * but does NOT define a wire enum of source slugs — so this is a string, and
 * `null` means "not recognized".
 *
 * Surfaced on the response because the wizard tells the user what it detected,
 * and because it explains why a mapping arrived with no AI call.
 */
export const detectedSourceSchema = z.string().nullable();

// ---------------------------------------------------------------------------
// The mapping response
// ---------------------------------------------------------------------------

/**
 * What `POST /imports/jobs/{jobId}/mapping` returns.
 *
 * ⚠️ THE ENVELOPE IS A PROPOSAL, NOT A TRANSCRIPTION — treat it differently
 * from everything else in this package. §5.1's normative artifact is the FLAT
 * `header → { field, confidence }` map above (that is literally all its JSON
 * example contains), and §4.2 says only "returns proposed mapping". The two
 * extra fields below are NOT in the SPEC:
 *
 *   - `detectedSource` — §5.2 defines fingerprinting as a *behavior* ("we can
 *     pre-fill the mapping without calling the AI at all"), not a response
 *     field. Proposed because the wizard tells the user what it recognized.
 *   - `aiAssisted` — §5.1's outage path ("If Claude is down, the mapping step
 *     falls back to a manual UI … AI assists but never gates") is likewise a
 *     behavior. Proposed because "these came from a model" and "you are mapping
 *     this by hand" are different UI states, and inferring the difference from
 *     an empty map is fragile.
 *
 * Both are defensible, and both are still ME ADDING FIELDS TO A SPEC-FINAL
 * CONTRACT — the same thing this file refuses to do for `sourceFileHint`
 * above. Consistency demands the same treatment: **Milestone C/D should either
 * get §5.1 amended to include them, or drop them.** Until then, consume
 * `mapping` (which IS the SPEC) and treat these two as provisional.
 *
 * `MAPPING_RESPONSE_SPEC_FINAL_KEYS` below names the subset that is genuinely
 * spec-final, so a consumer can assert against it.
 */
export const mappingResponseSchema = z.object({
  mapping: proposedMappingSchema,
  /** PROVISIONAL (not in §5.1) — §5.2's recognized source, null if unrecognized. */
  detectedSource: detectedSourceSchema.optional(),
  /**
   * PROVISIONAL (not in §5.1). False when the mapping came from a fingerprint
   * (§5.2) or when the mapping call failed and the user maps manually (§5.1).
   */
  aiAssisted: z.boolean().optional(),
});
export type MappingResponse = z.infer<typeof mappingResponseSchema>;

/**
 * The part of the mapping response SPEC §5.1 actually specifies. Anything else
 * on the envelope is provisional — see the note above.
 */
export const MAPPING_RESPONSE_SPEC_FINAL_KEYS = ['mapping'] as const;

// ---------------------------------------------------------------------------
// PE-1904 — mapping provenance (§3.8's per-column confidence distribution)
// ---------------------------------------------------------------------------

/**
 * ADDED BY PE-1904, additively. The durable summary of how a confirmed mapping
 * was arrived at — see the `provenance` field on `mappingConfigSchema` below
 * for why it exists and why the proposal it summarizes stays disposable.
 *
 * SHAPE RECONCILED WITH PE-1896's ADAPTER, field for field. That adapter's
 * `ConfidenceDistribution` type carries `byHeader`, `tierCounts`, `model`,
 * `resolvedProvider`; all four appear here under the same names and the same
 * meanings, so the adapter's value can be persisted without a translation
 * layer that could silently reinterpret a number. Deliberately NOT renamed to
 * anything "nicer" — a rename is exactly the drift that makes two dashboards
 * disagree.
 *
 * `engine` is the one field added beyond that adapter's type. O5's
 * `import.mapping_proposed` payload carries `engine` as a required property,
 * and without a durable copy the job record cannot answer "which engine
 * produced this?" once the request that made the proposal is gone — which is
 * the exact question a mapping-quality dashboard splits on. Free-form for the
 * same reason O5 keeps it free-form: §5.1's engine internals are unpinned.
 *
 * NOTHING HERE IS A ROW OR A VALUE. Headers are file column names, not donor
 * data. That is a standing obligation, not an incidental property: this object
 * is copied verbatim into a PostHog payload, so a future field carrying a
 * sample cell value would ship donor PII to a third party.
 */
export const mappingProvenanceSchema = z.object({
  /**
   * Source column header → the confidence the engine reported for it, in
   * [0, 1]. Keyed by the RAW header exactly as `proposedMappingSchema` is, so
   * the two are joinable.
   *
   * A column the engine never judged (filtered out as a non-column, or arriving
   * from an identity map) is recorded as `0` by PE-1896's adapter rather than
   * omitted. Consumers must therefore not read `0` as "the engine was sure this
   * was wrong" — read the tier counts for the distribution, and this map for
   * per-column detail.
   */
  byHeader: z.record(z.string(), z.number().min(0).max(1)),
  /**
   * The same proposals bucketed into the annex's port-facing bands. Named
   * `AUTO`/`REVIEW`/`MANUAL` (not `high`/`medium`/`low`) to match both
   * `ports.ts` and PE-1896's adapter; the numeric boundaries stay in
   * `mappingConfidenceTier()` above and are NOT re-declared here.
   *
   * NOT refined to sum to the column count. A refinement would make a
   * slightly-off summary fail to PARSE, which would reject the whole
   * `mappingConfig` and therefore block a legitimate commit over a telemetry
   * detail — the same "assists, never gates" reasoning O5 applies to its own
   * tier counts.
   */
  tierCounts: z.object({
    AUTO: z.number().int().nonnegative(),
    REVIEW: z.number().int().nonnegative(),
    MANUAL: z.number().int().nonnegative(),
  }),
  /** Free-form engine identifier, matching O5's `engine`. */
  engine: z.string().optional(),
  /** Which model answered; `null` on the §5.1 degraded path. */
  model: z.string().nullable().optional(),
  /** The upstream provider the gateway resolved to — the ZDR pin's receipt. */
  resolvedProvider: z.string().nullable().optional(),
});
export type MappingProvenance = z.infer<typeof mappingProvenanceSchema>;

// ---------------------------------------------------------------------------
// The confirmed mapping, as stored on the job record (§3.1 `mappingConfig`)
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 types the job record's `mappingConfig` as a Map whose "shape [is]
 * defined in §5.1", and §4.2 says the confirmed mapping "is saved to the job
 * record (`mappingConfig`)".
 *
 * It is NOT the same object as the *proposal*: what gets stored is the user's
 * confirmed column→field decision, plus §5.3's file-level default phone region,
 * which §5.3 states is "recorded on the job record alongside `mappingConfig` so
 * the import stays reproducible." Modeled here as a sibling field inside the
 * config so the two cannot be persisted apart.
 *
 * NOTE ON PROVENANCE: §5.3's file-level-default-region rule and §5.4.1/§5.4.2's
 * validation tables arrived on the `docs/import-tool-validation-rules` branch,
 * which was NOT on dev when this contract was written — it is merged into the
 * same PR that adds this file precisely so these citations resolve. If you are
 * reading this on a branch where SPEC §5.4 has no subsections, that merge is
 * missing and the citations below will not resolve.
 *
 * `columns` drops the confidence score: once confirmed, a column's mapping is
 * a decision, not a guess. Retaining the original proposal is an audit concern
 * §3.1 does not ask for.
 */
export const mappingConfigSchema = z.object({
  /**
   * Confirmed column header → target field, or null for a column the user
   * chose to skip (§4.2's third option). Null is retained rather than the key
   * being dropped, so a re-opened wizard can show "skipped" instead of
   * re-proposing.
   */
  columns: z.record(z.string(), z.string().nullable()),
  /**
   * §5.3's **file-level default region** for bare national phone numbers, as an
   * ISO-3166-1 alpha-2 country code. §5.3: the wizard "asks for a default
   * region whenever the file contains phones lacking a `+` or `00` prefix,
   * pre-filled from the org's country" — so it is absent when the file had no
   * such phones. §5.4.2 pairs it with a bare-national-format warning, and
   * §5.4.3 explains the hazard it exists to close: `clean_phone_number` parses
   * with a hardcoded US default, so a bare `5551234567` "parses successfully as
   * US" and a non-US file's phones are silently rewritten to wrong US numbers —
   * no parse failure, nothing for an "unparseable" rule to catch.
   *
   * NORMALIZED then validated, rather than either one alone:
   *   - a bare `.length(2)` accepts `"Z9"` / `"9!"`, which then silently fail to
   *     resolve in libphonenumber — reintroducing the silent-wrong-country bug
   *     above;
   *   - a bare `/^[A-Z]{2}$/` REJECTS `"us"`, which is plausible input: §5.3
   *     says the value is "pre-filled from the org's country", and nothing
   *     constrains that field's case (§5.4.3 notes address `country` is stored
   *     as supplied and deliberately NOT normalized). A hard commit-time
   *     failure on `"us"` would be a contract bug, not validation.
   *
   * So: trim, uppercase, then require two letters. `"us"` → `"US"`; `"Z9"` and
   * `"USA"` still fail. This does NOT check ISO-3166 membership — `"ZZ"` passes
   * the shape check — the region list belongs to libphonenumber, not here.
   */
  defaultPhoneRegion: z
    .string()
    .trim()
    .toUpperCase()
    .pipe(
      z
        .string()
        .regex(/^[A-Z]{2}$/, 'must be a two-letter ISO-3166-1 alpha-2 country code'),
    )
    .optional(),
  /** §5.2's detected source, retained so the import stays reproducible. */
  detectedSource: detectedSourceSchema.optional(),
  /**
   * ADDED BY PE-1904 (funnel instrumentation), additively — a new optional
   * field, no existing field's meaning changed.
   *
   * WHY IT EXISTS. SPEC §3.8 names the **per-column confidence distribution**
   * as one of two quality metrics needing a durable home, and
   * `decisions.md#D-FUNNEL-TRANSPORT` decides where: "persist the distribution
   * summary in `MappingConfig.provenance` rather than retaining proposals."
   * The two halves of that sentence are equally binding — **the proposal itself
   * stays disposable**. This field is a SUMMARY of the proposal, deliberately
   * not the proposal: `columns` above still "drops the confidence score, once
   * confirmed a column's mapping is a decision, not a guess", and nothing here
   * reinstates a per-column re-litigation of that decision. What it reinstates
   * is the aggregate a dashboard needs and a confirmed map destroys.
   *
   * WHY NOT LEAVE IT ON THE ADAPTER. PE-1896's OpenRouter adapter already
   * computes exactly this shape and surfaces it on its own result type,
   * explicitly declining to edit this contract. That is the right call for an
   * adapter but it leaves the metric homeless: the adapter result is
   * request-scoped and discarded, so a distribution that never lands on the
   * job record is not durable, and D-FUNNEL-TRANSPORT's whole point is that the
   * job record — not telemetry — is the source of truth. So the field lands
   * here, shaped to match what that adapter already produces.
   *
   * OPTIONAL, AND CONSUMERS MUST TOLERATE ITS ABSENCE. It is absent on every
   * job written before this field existed, on any Tier-0/1 identity map that
   * never called a model (§4.0.1), and on the §5.1 provider-degraded path where
   * the user mapped by hand. Absent means "no distribution was recorded", which
   * is a different and weaker statement than "all columns were low confidence" —
   * a metric that conflates the two would report a mapping-quality collapse
   * every time the template on-ramp worked perfectly.
   */
  provenance: mappingProvenanceSchema.optional(),
  /**
   * ADDED BY PE-1899, additively — a new optional field; `columns` above is
   * untouched and keeps its exact meaning.
   *
   * WHY IT EXISTS. `columns` is `Record<header, target|null>`, and the annex's
   * O3 conformance bullet (§7) requires four properties that shape cannot
   * carry: **totality over columns · explicit `SKIP` · single-valued-target
   * uniqueness · `origin` present on every column.** Specifically it has no
   * `columnIndex` (so two identically-spelled headers COLLAPSE to one key —
   * the same defect PE-1896's OpenRouter adapter escalated for
   * `MappingProposal.proposals`), no `origin` (so SPEC §3.8's mapping-override
   * rate is not computable straight from DynamoDB, which is the whole reason
   * `origin` is required in the first place), and no slot for the per-column
   * `transform` a consent column must carry.
   *
   * REDEFINING `columns` WOULD BE A BREAKING SEAM CHANGE (annex §8) — every
   * job written before this field existed carries the record shape. So the
   * durable, annex-conformant column list lands HERE, as a sibling, and
   * `columns` is retained and still written alongside it as the flat
   * projection older readers already understand. Both are written by
   * `imports.confirmMapping` in the same conditional update, so they can never
   * be written from DIFFERENT mappings or land at different times.
   *
   * ⚠️ THAT ATOMICITY IS NOT AGREEMENT ON CONTENT — `confirmedColumns` IS THE
   * AUTHORITY. `columns` is a `Record`, so it holds one slot per header, and a
   * file with two identically-spelled headers mapped to different targets
   * (legal under O3, which keys on `columnIndex`) simply cannot be expressed in
   * it: one of the two mappings is ABSENT from `columns` while both are present
   * in `confirmedColumns`. `toFlatColumnRecord` keeps the FIRST occurrence, so
   * the dropped one is the later duplicate. A consumer that reads only
   * `columns` on a duplicate-header file will be silently wrong about which
   * column carries e.g. the donor's email. Read `confirmedColumns` when it is
   * present; fall back to `columns` only for jobs that predate it.
   *
   * FIELD-FOR-FIELD IDENTICAL to `denormalizedColumnMappingSchema` (the copy
   * O2's header line already carries). Deliberately the same shape rather than
   * a parallel one: the header line is a denormalized COPY of exactly this,
   * and two schemas that are supposed to agree but are declared separately are
   * two schemas that will eventually disagree.
   *
   * OPTIONAL, AND CONSUMERS MUST TOLERATE ITS ABSENCE — it is absent on every
   * job confirmed before this field existed. Absence means "this job predates
   * the durable column list", NOT "this job mapped no columns".
   */
  confirmedColumns: z.array(denormalizedColumnMappingSchema).optional(),
});
export type MappingConfig = z.infer<typeof mappingConfigSchema>;
